import { graphql, useFragment } from "react-relay";
import { WidgetNewsfeedPost_widgetNewsfeedPost$key } from "./__generated__/WidgetNewsfeedPost_widgetNewsfeedPost.graphql";
import { AHText, Box, useTime } from "arkh";
import { Image } from "react-native";
import { ComponentType, ReactElement, useMemo } from "react";
import { DateTime } from "luxon";

interface Props {
  post: WidgetNewsfeedPost_widgetNewsfeedPost$key;
  height?: number;
  active?: boolean;
  ImageWrapper: ComponentType<{
    children: ReactElement[];
    active: boolean;
    onNextPost?: () => void;
  }>;
  onNextPost?: () => void;
}

export const WidgetNewsfeedPost = (props: Props) => {
  const post = useFragment(
    graphql`
      fragment WidgetNewsfeedPost_widgetNewsfeedPost on WidgetNewsfeedPost {
        title
        body
        images {
          id
          largeImage: size(minHeight: 1080, minWidth: 1080) {
            url
            height
            width
          }
        }
        date
      }
    `,
    props.post
  );

  const now = useTime(60000);

  const formattedDate = useMemo(() => {
    if (!post.date) return undefined;

    const date = DateTime.fromISO(post.date);
    if (now.startOf("day") < date) {
      return date.toLocaleString(DateTime.TIME_24_SIMPLE);
    } else {
      return date.toLocaleString({
        ...DateTime.DATETIME_MED_WITH_WEEKDAY,
        year: undefined,
      });
    }
  }, [now, post.date]);

  const ImageWrapper = props.ImageWrapper;

  return (
    <Box
      style={{
        flexDirection: "column",
        width: "100%",
        height: props.height ?? "100%",
      }}
      space="small"
    >
      {post.title && <AHText space="small">{post.title}</AHText>}
      {post.body && (
        <Box space="small">
          <AHText numberOfLines={5}>{post.body}</AHText>
        </Box>
      )}
      <Box style={{ flex: 1 }} spaceTop="small">
        <ImageWrapper
          active={props.active ?? false}
          onNextPost={props.onNextPost}
        >
          {post.images.map((image, i) => (
            <Image
              key={i}
              style={{
                height: "100%",
                width: "100%",
              }}
              source={{ uri: image.largeImage?.url }}
              resizeMode="contain"
            />
          ))}
        </ImageWrapper>
      </Box>
      {formattedDate && (
        <AHText type="detail" spaceTop="small">
          {formattedDate}
        </AHText>
      )}
    </Box>
  );
};
